import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ExternalLink, Play, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function StudioPage() {
  const { slug } = useParams();
  const [studio, setStudio] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStudioData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadStudioData = async () => {
    try {
      // Get studio and videos from public endpoint
      const response = await api.get(`/api/videos/public/studio/${slug}`);
      const { studio, videos } = response.data;
      
      setStudio(studio);
      setVideos(videos);
    } catch (error) {
      console.error("Error loading studio:", error);
      if (error.response?.status === 404) {
        toast.error("Studio not found");
      } else {
        toast.error("Failed to load studio data");
      }
    } finally {
      setLoading(false);
    }
  };

  const trackClick = async (video) => {
    try {
      await api.post("/api/videos/track-studio-click", {
        video_id: video.id,
        studio_name: studio.stage_name
      });
    } catch (e) {
      // Silent fail
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (!studio) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Studio Not Found</h1>
          <Link to="/" className="text-pink-500 hover:text-pink-400">
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Studio Header */}
        <div className="mb-8">
          {studio.photos && studio.photos.length > 0 && (
            <img
              src={studio.photos[0]}
              alt={studio.stage_name}
              className="h-24 mb-4"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <h1 className="text-4xl font-bold mb-2">{studio.stage_name}</h1>
          {studio.studio_website && (
            <a
              href={studio.studio_website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 flex items-center gap-2"
            >
              Visit Website <ExternalLink size={16} />
            </a>
          )}
        </div>

        {/* Video Grid */}
        <div>
          <h2 className="text-2xl font-bold mb-4">
            Scenes from {studio.stage_name} ({videos.length})
          </h2>
          
          {videos.length === 0 ? (
            <p className="text-zinc-400">No scenes available yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map((video) => (
                <div key={video.id} className="bg-zinc-900 rounded-lg overflow-hidden border border-zinc-800 hover:border-pink-500/50 transition">
                  {/* Thumbnail */}
                  <Link to={`/video/${video.id}/preview`}>
                    <div className="relative aspect-video bg-zinc-800">
                      {video.cover_image ? (
                        <img
                          src={video.cover_image}
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Play size={48} className="text-zinc-600" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition flex items-center justify-center">
                        <Play size={64} className="text-white" />
                      </div>
                    </div>
                  </Link>
                  
                  {/* Info */}
                  <div className="p-4">
                    <h3 className="font-semibold text-white mb-2 truncate">{video.title || "Untitled"}</h3>
                    
                    {video.studio_scene_url ? (
                      <a
                        href={video.studio_scene_url}
                        target="_blank"
                        rel="noopener sponsored"
                        onClick={() => trackClick(video)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition"
                      >
                        Watch on {studio.stage_name} <ExternalLink size={14} />
                      </a>
                    ) : studio.studio_website && (
                      <a
                        href={studio.studio_website}
                        target="_blank"
                        rel="noopener sponsored"
                        onClick={() => trackClick(video)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition"
                      >
                        Visit {studio.stage_name} <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
